import { useCallback, useState } from 'react';
import { getBoundedValue } from 'utils/math/math.utils';
import {
  getAvailableVideoTimeRange,
  getSeekableVideoTimeRange,
  skipToNewestReadyVideoWithCushion,
} from '../utils/videoPlayer.utils';

const LIVE_EDGE_CUSHION_SECONDS = 0.25;

/**
 * Calculates how much loaded video is available around the current playback position.
 *
 * The hook reads the video element's buffered and seekable ranges, updates progress
 * and buffer values for the controls, can jump near the newest loaded video, and can
 * seek to a fraction of the available video range.
 */
const useReadyVideoProgress = ({ videoRef }) => {
  const [progress, setProgress] = useState(0); // 0-1 amount of video user has watched where 1 second or less is 100%
  const [bufferedProgress, setBufferedProgress] = useState(0); // 0–1 buffer value based on ready-to-play seconds ahead; 5+ seconds counts as 100%
  const [bufferedSecondsAhead, setBufferedSecondsAhead] = useState(0); // Number of seconds of downloaded video ahead of current user position

  const resetReadyVideoProgress = useCallback(() => {
    setProgress(0);
    setBufferedProgress(0);
    setBufferedSecondsAhead(0);
  }, []);

  const updateReadyVideoProgress = useCallback(() => {
    const video = videoRef.current;
    const readyVideoRange = getAvailableVideoTimeRange(video);
    const seekableVideoRange = getSeekableVideoTimeRange(video);

    if (!video || !seekableVideoRange) {
      resetReadyVideoProgress();
      return;
    }

    const currentTime = Number.isFinite(video.currentTime)
      ? video.currentTime
      : seekableVideoRange.earliestAvailableTimestamp;

    const bufferedSecondsAhead = readyVideoRange
      ? Math.max(0, readyVideoRange.latestAvailableTimestamp - currentTime)
      : 0;

    setBufferedSecondsAhead(bufferedSecondsAhead);
    setBufferedProgress(Math.max(0, Math.min(1, bufferedSecondsAhead / 5))); // 0 to 1 value based on a fraction of 5 seconds where 5+ is 1 (100%)
    setProgress(
      Math.max(
        0,
        Math.min(
          1,
          (currentTime - seekableVideoRange.earliestAvailableTimestamp) /
            seekableVideoRange.availableVideoDuration,
        ),
      ),
    );
  }, [resetReadyVideoProgress, videoRef]);

  const skipToNewestReadyVideo = useCallback(() => {
    const didSkip = skipToNewestReadyVideoWithCushion(videoRef.current, LIVE_EDGE_CUSHION_SECONDS);

    if (didSkip) {
      updateReadyVideoProgress();
    }

    return didSkip;
  }, [updateReadyVideoProgress, videoRef]);

  const seekToProgress = useCallback(
    (nextProgress) => {
      const video = videoRef.current;
      const seekableVideoRange = getSeekableVideoTimeRange(video);

      if (!video || !seekableVideoRange || !Number.isFinite(nextProgress)) return false;

      const { earliestAvailableTimestamp, availableVideoDuration } = seekableVideoRange;
      const boundedProgress = getBoundedValue(nextProgress, 0, 1);
      video.currentTime = earliestAvailableTimestamp + boundedProgress * availableVideoDuration;

      updateReadyVideoProgress();
      return true;
    },
    [updateReadyVideoProgress, videoRef],
  );

  return {
    progress,
    bufferedProgress,
    bufferedSecondsAhead,
    resetReadyVideoProgress,
    updateReadyVideoProgress,
    skipToNewestReadyVideo,
    seekToProgress,
  };
};

export default useReadyVideoProgress;
